import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { getInitialBadgeColor } from '@/lib/utils/color.utils';
import type { AttendeeListItem } from '@/lib/types/attendance.types';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { cn } from '@/lib/utils';
import { useState } from 'react';

export interface AttendeeDrawerProps {
  /** Controls whether the drawer is open */
  open: boolean;
  /** Callback when the drawer open state changes */
  onOpenChange: (open: boolean) => void;
  /** The attendee to display */
  attendee: AttendeeListItem | null;
  /** Whether to show the remove button (only for attendance tab) */
  showRemoveButton: boolean;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Whether removal is in progress */
  isRemoving?: boolean;
}

/**
 * Formats a user role for display
 */
function formatRole(role: string | null): string {
  if (!role) return 'No role assigned';
  return getRoleLabelByKey(role);
}

/**
 * Drawer component that displays attendee information
 * Shows name, email, role, and optionally a remove button
 */
export function AttendeeDrawer({
  open,
  onOpenChange,
  attendee,
  showRemoveButton,
  onRemove,
  isRemoving = false,
}: AttendeeDrawerProps) {
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);

  if (!attendee) {
    return null;
  }

  const badgeColor = getInitialBadgeColor(attendee.name);
  const roleTone = getGroupRoleTone(attendee.role);
  const handleRemoveClick = () => {
    setIsConfirmRemoveOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (onRemove) {
      try {
        await onRemove();
        setIsConfirmRemoveOpen(false);
        onOpenChange(false);
      } catch {
        // Error is handled by the mutation, just close the confirmation drawer
        setIsConfirmRemoveOpen(false);
      }
    }
  };

  return (
    <>
      <ActionDrawer
        open={isConfirmRemoveOpen}
        onOpenChange={setIsConfirmRemoveOpen}
        title="Remove from attendance?"
        description="This will remove this person from the attendance list. This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={handleConfirmRemove}
        isLoading={isRemoving}
        loadingLabel="Removing..."
      />
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <div className="mx-auto w-full max-w-sm">
            <DrawerHeader>
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-md text-large text-slate-50 ${badgeColor}`}
                >
                  {attendee.initials}
                </span>
                <DrawerTitle className="text-[24px]">
                  {attendee.name}
                </DrawerTitle>
              </div>
              <div className="space-y-3 pt-2">
                <div>
                  <DrawerDescription className="text-small text-slate-400 mb-1">
                    Email
                  </DrawerDescription>
                  <p className="text-body text-slate-50">
                    {attendee.email || 'No email provided'}
                  </p>
                </div>
                <div>
                  <DrawerDescription className="text-small text-slate-400 mb-1">
                    User Type
                  </DrawerDescription>
                  <p
                    className={cn(
                      'inline-flex rounded-full border px-2 py-0.5 font-field-42 text-[10px] uppercase tracking-wide',
                      roleTone.pillClassName
                    )}
                  >
                    {formatRole(attendee.role)}
                  </p>
                </div>
              </div>
            </DrawerHeader>
            {showRemoveButton && (
              <DrawerFooter>
                <Button
                  onClick={handleRemoveClick}
                  disabled={isRemoving}
                  variant="destructive"
                  className="h-16"
                >
                  {isRemoving ? 'Removing...' : 'Remove from attendance'}
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline" className="h-16">
                    Close
                  </Button>
                </DrawerClose>
              </DrawerFooter>
            )}
            {!showRemoveButton && (
              <DrawerFooter>
                <DrawerClose asChild>
                  <Button variant="outline" className="h-16">
                    Close
                  </Button>
                </DrawerClose>
              </DrawerFooter>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
